import { useEffect, useId, useMemo, useState } from 'react';
import AsyncBoundary from '../AsyncBoundary';
import { useAsync } from '../../../hooks/useAsync';
import { toFa } from '../../../utils/faFormat';

const DEFAULT_LABEL = (option) => option.name || option.display_name || option.service_type || '';

export default function EntityPicker({
  id,
  label,
  value,
  onChange,
  loadOptions,
  getOptionLabel = DEFAULT_LABEL,
  disabled = false,
  placeholder = 'جست‌وجو و انتخاب',
  emptyMessage = 'گزینه‌ای برای انتخاب وجود ندارد. ابتدا مرجع مربوط را ثبت کنید.',
  deps = [],
  onLoad,
}) {
  const generatedId = useId().replace(/:/g, '');
  const inputId = id || `entity-picker-${generatedId}`;
  const listboxId = `${inputId}-listbox`;
  const statusId = `${inputId}-status`;
  const asyncState = useAsync(loadOptions, deps);
  const options = useMemo(
    () => asyncState.data?.carriers || asyncState.data?.services || asyncState.data || [],
    [asyncState.data],
  );
  const selected = options.find((option) => option.id === value) || null;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setQuery(selected ? getOptionLabel(selected) : '');
  }, [selected, getOptionLabel]);

  useEffect(() => {
    if (asyncState.status === 'ready') onLoad?.(options);
  }, [asyncState.status, onLoad, options]);

  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase('fa');
    if (!term || selected?.id === value) return options;
    return options.filter((option) => getOptionLabel(option).toLocaleLowerCase('fa').includes(term));
  }, [getOptionLabel, options, query, selected, value]);

  useEffect(() => {
    if (!open) setActiveIndex(-1);
    else if (activeIndex >= filtered.length) setActiveIndex(filtered.length ? filtered.length - 1 : -1);
  }, [activeIndex, filtered.length, open]);

  const choose = (option) => {
    setQuery(getOptionLabel(option));
    setOpen(false);
    setActiveIndex(-1);
    onChange(option);
  };

  const move = (nextIndex) => {
    if (!filtered.length) return;
    setOpen(true);
    setActiveIndex(Math.max(0, Math.min(nextIndex, filtered.length - 1)));
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(open ? activeIndex + 1 : 0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(open ? (activeIndex < 0 ? filtered.length - 1 : activeIndex - 1) : filtered.length - 1);
    } else if (event.key === 'Home' && open) {
      event.preventDefault();
      move(0);
    } else if (event.key === 'End' && open) {
      event.preventDefault();
      move(filtered.length - 1);
    } else if (event.key === 'Enter' && open && activeIndex >= 0) {
      event.preventDefault();
      choose(filtered[activeIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery(selected ? getOptionLabel(selected) : '');
    }
  };

  return (
    <div className="coverage-entity-picker">
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-describedby={statusId}
        aria-activedescendant={open && activeIndex >= 0 ? `${inputId}-option-${activeIndex}` : undefined}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActiveIndex(-1);
          if (value) onChange(null);
        }}
        onKeyDown={onKeyDown}
      />
      <span id={statusId} className="coverage-entity-status" aria-live="polite">
        {asyncState.status === 'loading' && 'در حال دریافت گزینه‌ها…'}
        {asyncState.status === 'error' && 'دریافت گزینه‌ها ناموفق بود.'}
        {asyncState.status === 'ready' && (selected
          ? `${getOptionLabel(selected)} انتخاب شد.`
          : `${toFa(filtered.length)} گزینه در دسترس است.`)}
      </span>
      {open && !disabled && (
        <div className="coverage-entity-popover">
          <AsyncBoundary
            status={asyncState.status}
            error={asyncState.error}
            retry={asyncState.retry}
            isEmpty={asyncState.status === 'ready' && options.length === 0}
            emptyMessage={emptyMessage}
          >
            {filtered.length ? (
              <ul id={listboxId} role="listbox" aria-label={`گزینه‌های ${label}`}>
                {filtered.map((option, index) => {
                  const isSelected = option.id === value;
                  return (
                    <li
                      id={`${inputId}-option-${index}`}
                      key={option.id}
                      role="option"
                      aria-selected={isSelected}
                      className={index === activeIndex ? 'is-active' : ''}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => choose(option)}
                    >
                      <span>{getOptionLabel(option)}</span>
                      {isSelected && <strong>انتخاب‌شده</strong>}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="coverage-entity-empty" role="status">
                نتیجه‌ای برای «{query}» نبود. عبارت جست‌وجو را تغییر دهید.
              </div>
            )}
          </AsyncBoundary>
        </div>
      )}
    </div>
  );
}
